// AlertContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import Alert, { AlertProps, AlertSeverity } from './Alert';
import { logError } from './logger';

export interface ShowAlertOptions {
  severity: AlertSeverity;
  title: string;
  message: string;
  actions?: AlertProps['actions'];
  onClose?: () => void;
}

interface AlertContextValue {
  showAlert: (options: ShowAlertOptions) => void;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

export const useAlert = (): AlertContextValue => {
  const context = useContext(AlertContext);
  if (!context) {
    logError('useAlert', 'AlertContext is undefined. Ensure you are using AlertProvider.');
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = useCallback(({ severity, title, message, actions, onClose }: ShowAlertOptions) => {
    
    window.parent.postMessage({ action: 'scrollToTop' }, '*');

    setAlert({ 
        severity,
        title,
        message,
        actions,
        onClose: onClose || (() => setAlert(null)),
    });
  }, []);

  const handleClose = useCallback(() => {
    if (alert?.onClose) {
      alert.onClose();
    }
    setAlert(null);
  }, [alert]);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {alert && (
        <Alert
          severity={alert.severity}
          title={alert.title}
          message={alert.message}
          actions={alert.actions}
          onClose={handleClose}
        />
      )}
    </AlertContext.Provider>
  );
};
